import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Trash2, Plus, Upload, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, formatErr, mediaUrl } from "@/lib/api";

export const getPath = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
export const setPath = (obj, path, val) => {
  const out = structuredClone(obj);
  const keys = path.split(".");
  let o = out;
  keys.slice(0, -1).forEach((k) => {
    if (o[k] == null) o[k] = {};
    o = o[k];
  });
  o[keys[keys.length - 1]] = val;
  return out;
};
const registry = {};
const tid = (s) => String(s).replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
const inputCls = "w-full rounded-xl border border-bt-border bg-white px-4 py-3 text-[15px] text-bt-heading outline-none focus:border-bt-primary";

export function MediaInput({ value, onChange, image, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(formatErr(e));
    }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input className={inputCls} value={value || ""} placeholder="Paste a URL or upload" onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        <button type="button" onClick={() => ref.current.click()} className="flex shrink-0 items-center gap-2 rounded-xl border border-bt-primary px-4 text-sm font-semibold text-bt-primary hover:bg-bt-sage" data-testid={`${testid}-upload`}>
          <Upload size={15} /> {busy ? "..." : "Upload"}
        </button>
        <input ref={ref} type="file" className="hidden" accept={image ? "image/*" : undefined} onChange={(e) => upload(e.target.files[0])} />
      </div>
      {image && value && <img src={mediaUrl(value)} alt="" className="h-20 rounded-xl border border-bt-border object-cover" />}
    </div>
  );
}

function TTSInput({ f, v, set, whole, testid }) {
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    const text = (f.textFrom ? f.textFrom(whole) : "").trim();
    if (!text) return toast.error("Add some text first");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tts", { text: text.slice(0, 4000), voice: f.voice || "coral" });
      set(data.url);
      toast.success("Voice recording created. Remember to save.");
    } catch (e) {
      toast.error(formatErr(e));
    }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      <MediaInput value={v} onChange={set} testid={testid} />
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={gen} disabled={busy} className="rounded-xl bg-bt-primary px-4 py-2 text-sm font-semibold text-bt-bg disabled:opacity-60" data-testid={`${testid}-generate`}>
          {busy ? "Creating voice..." : v ? "Re-generate AI voice" : "Generate AI voice"}
        </button>
        {v && <audio src={mediaUrl(v)} controls className="h-10" />}
      </div>
    </div>
  );
}

function ResourcePicker({ f, v = [], set, testid }) {
  const [all, setAll] = useState([]);
  useEffect(() => {
    api.get("/admin/resources").then((r) => setAll(r.data));
  }, []);
  const by = Object.fromEntries(all.map((x) => [x.slug, x]));
  const rest = all.filter((x) => !v.includes(x.slug) && (!f.types || f.types.includes(x.type)));
  return (
    <div className="space-y-2" data-testid={testid}>
      {v.map((slug, i) => (
        <div key={slug} className="flex items-center gap-1 rounded-xl border border-bt-border bg-white px-3 py-2 text-sm" data-testid={`${testid}-item-${slug}`}>
          <span className="w-6 text-bt-sagedeep">{i + 1}.</span>
          <span className="flex-1 font-medium text-bt-heading">{by[slug]?.title || slug}{by[slug] && !by[slug].audio_url && <span className="ml-2 text-xs text-red-700">no audio yet</span>}</span>
          <button type="button" className="rounded-lg p-2 hover:bg-bt-sage" onClick={() => set(move(v, i, -1))} aria-label="Move up" data-testid={`${testid}-item-${slug}-up`}><ArrowUp size={15} /></button>
          <button type="button" className="rounded-lg p-2 hover:bg-bt-sage" onClick={() => set(move(v, i, 1))} aria-label="Move down" data-testid={`${testid}-item-${slug}-down`}><ArrowDown size={15} /></button>
          <button type="button" className="rounded-lg p-2 text-red-700 hover:bg-red-50" onClick={() => set(v.filter((x) => x !== slug))} aria-label="Remove" data-testid={`${testid}-item-${slug}-remove`}><Trash2 size={15} /></button>
        </div>
      ))}
      <select className={inputCls} value="" onChange={(e) => e.target.value && set([...v, e.target.value])} data-testid={`${testid}-add`}>
        <option value="">+ Add an affirmation...</option>
        {rest.map((x) => <option key={x.slug} value={x.slug}>{x.title}</option>)}
      </select>
    </div>
  );
}

function Input({ f, v, set, whole, testid }) {
  switch (f.type) {
    case "resources":
      return <ResourcePicker f={f} v={v} set={set} testid={testid} />;
    case "tts":
      return <TTSInput f={f} v={v} set={set} whole={whole} testid={testid} />;
    case "textarea":
      return <textarea className={`${inputCls} min-h-[110px]`} rows={f.rows || 4} value={v || ""} onChange={(e) => set(e.target.value)} data-testid={testid} />;
    case "strings":
      return <textarea className={`${inputCls} min-h-[110px]`} rows={f.rows || 5} value={(v || []).join("\n")} placeholder="One per line" onChange={(e) => set(e.target.value.split("\n"))} onBlur={() => set((v || []).map((x) => x.trim()).filter(Boolean))} data-testid={testid} />;
    case "number":
      return <input type="number" step="any" className={inputCls} value={v ?? ""} onChange={(e) => set(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testid} />;
    case "bool":
      return <Switch checked={!!v} onCheckedChange={set} data-testid={testid} />;
    case "color":
      return (
        <div className="flex gap-2">
          <input type="color" value={v || "#000000"} onChange={(e) => set(e.target.value)} className="h-12 w-14 cursor-pointer rounded-xl border border-bt-border" data-testid={`${testid}-picker`} />
          <input className={inputCls} value={v || ""} onChange={(e) => set(e.target.value)} data-testid={testid} />
        </div>
      );
    case "select":
      return (
        <select className={inputCls} value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={testid}>
          {(f.options || []).map((o) => {
            const op = typeof o === "string" ? { value: o, label: o } : o;
            return <option key={op.value} value={op.value}>{op.label}</option>;
          })}
        </select>
      );
    case "image":
    case "media":
      return <MediaInput value={v} onChange={set} image={f.type === "image"} testid={testid} />;
    case "list":
      return registry.list({ f, items: v || [], onChange: set, testid });
    default:
      return <input className={inputCls} value={v ?? ""} onChange={(e) => set(e.target.value)} data-testid={testid} />;
  }
}

export function FormFields({ schema, value, onChange, prefix = "field" }) {
  return (
    <div className="grid gap-5 md:grid-cols-2">
      {schema.filter((f) => !f.show || f.show(value)).map((f) => (
        <div key={f.key} className={["textarea", "strings", "list", "image", "media", "tts", "resources"].includes(f.type) || f.full ? "md:col-span-2" : ""}>
          <label className="mb-2 block text-sm font-semibold text-bt-heading">{f.label}</label>
          {f.help && <p className="-mt-1 mb-2 text-xs text-bt-sagedeep">{f.help}</p>}
          <Input f={f} whole={value} v={getPath(value, f.key)} set={(nv) => onChange(setPath(value, f.key, nv))} testid={`${prefix}-${tid(f.key)}`} />
        </div>
      ))}
    </div>
  );
}

export const move = (arr, i, d) => {
  const a = [...arr];
  const j = i + d;
  if (j < 0 || j >= a.length) return a;
  [a[i], a[j]] = [a[j], a[i]];
  return a;
};

export function ListEditor({ f, items, onChange, testid }) {
  const [open, setOpen] = useState(null);
  const keys = useRef([]);
  while (keys.current.length < items.length) keys.current.push(crypto.randomUUID());
  keys.current.length = items.length;
  const change = (next, order) => {
    keys.current = order.map((j) => keys.current[j]);
    onChange(next);
  };
  const idx = items.map((_, j) => j);
  const title = (it, i) => (f.titleKey && it[f.titleKey]) || `${f.itemLabel || "Item"} ${i + 1}`;
  return (
    <div className="space-y-2" data-testid={`${testid}-list`}>
      {items.map((it, i) => (
        <div key={keys.current[i]} className="rounded-2xl border border-bt-border bg-white">
          <div className="flex items-center gap-1 px-3 py-2">
            <button type="button" onClick={() => setOpen(open === i ? null : i)} className="flex flex-1 items-center gap-2 py-1 text-left text-sm font-medium text-bt-heading" data-testid={`${testid}-item-${i}-toggle`}>
              {open === i ? <ChevronUp size={16} /> : <ChevronDown size={16} />} <span className="line-clamp-1">{title(it, i)}</span>
            </button>
            <button type="button" className="rounded-lg p-2 hover:bg-bt-sage" onClick={() => change(move(items, i, -1), move(idx, i, -1))} aria-label="Move up" data-testid={`${testid}-item-${i}-up`}><ArrowUp size={15} /></button>
            <button type="button" className="rounded-lg p-2 hover:bg-bt-sage" onClick={() => change(move(items, i, 1), move(idx, i, 1))} aria-label="Move down" data-testid={`${testid}-item-${i}-down`}><ArrowDown size={15} /></button>
            <button type="button" className="rounded-lg p-2 text-red-700 hover:bg-red-50" onClick={() => change(items.filter((_, j) => j !== i), idx.filter((j) => j !== i))} aria-label="Remove" data-testid={`${testid}-item-${i}-remove`}><Trash2 size={15} /></button>
          </div>
          {open === i && (
            <div className="border-t border-bt-border p-4">
              <FormFields schema={f.fields} value={it} onChange={(nv) => onChange(items.map((x, j) => (j === i ? nv : x)))} prefix={`${testid}-${i}`} />
            </div>
          )}
        </div>
      ))}
      <button type="button" onClick={() => { onChange([...items, structuredClone(f.newItem || {})]); setOpen(items.length); }}
        className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-bt-primary hover:bg-bt-sage" data-testid={`${testid}-add`}>
        <Plus size={16} /> Add {f.itemLabel || "item"}
      </button>
    </div>
  );
}

registry.list = (props) => <ListEditor {...props} />;
